import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import getDayRangeString from "../../../utils/day-range-string";
import fromIntensity from "../../../utils/get-zone";
import MetricsBackButton from "../../athlete-portal/metrics/metrics-back-button";
import set from "@rill/set";
import convertToRiR from "../../../utils/converters/convert-intensity";
import NavigationComponent from "../../athlete-portal/calendar/components/NavigationComponent";
import { ReportsBackHandler } from "../../athlete-portal/calendar/components/ReportsBackHandler";
import SvgIcon from "../../../parts/SvgIcon";

const {
  RPE_STANDARD_TABLE,
  DATE_FORMATS: { DATE_PICKER_FORMAT, DATE_DAY_FORMAT },
} = require("../../../constants");

const REP_RANGE = _.range(1, 13);
const RPE_RANGE = _.reverse(_.range(5, 10.5, 0.5));
const CALC_REP_RANGE = _.range(-12, 23);
const CALC_RPE_RANGE = _.reverse(_.range(-7, 22.5, 0.5));
const MAX_CALC_RPE = _.max(CALC_RPE_RANGE);
const MIN_CALC_RPE = _.min(CALC_RPE_RANGE);
const MAX_CALC_REP = _.max(CALC_REP_RANGE);
const MIN_CALC_REP = _.min(CALC_REP_RANGE);

function View(props, { locals }) {
  const {
    query,
    activeUser,
    activeUserToRequest,
    search,
    feBranding = {},
  } = locals;
  const activeMetrics = _.cloneDeep(
    _.get(locals, ["exercisesRPETable", query.type]) || {}
  );
  const activeTable =
    query.formula === "standard"
      ? RPE_STANDARD_TABLE
      : calculateEstimates(activeMetrics);
  const activeRepMax = _.get(locals.activeUser, `repMaximums.${query.type}`, 0);
  const intensityMeasure =
    _.get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";
  const isRiR = intensityMeasure === "RiR";
  const { mobileWV } = query;

  return (
    <main
      role="reports-wrapper"
      data-theme={mobileWV ? feBranding.appTheme : undefined}
      style={
        mobileWV
          ? {
              backgroundColor: "var(--background-color)",
              maxHeight: "unset",
              height: "100vh",
            }
          : null
      }
    >
      <NavigationComponent
        query={query}
        pathName={"/dashboard/metrics"}
        text="Calendar Day View"
        classNames="mobile-hidden"
      />
      <article role="report-content" id="strength-metrics">
        <ReportsBackHandler
          title={"Strength Metrics"}
          locals={locals}
          customBackUrl={`/dashboard/metrics/${search}${
            mobileWV ? "&&randomReload=true" : ""
          }`}
        />
        <header>
          <hgroup className="metrics-title">
            <MetricsBackButton
              url={`/dashboard/metrics/${search}`}
              classNames="mobile-hidden"
            />
            <SvgIcon
              src="/img/new-icons/rpe-custom-ico.svg"
              className="metrics-icon mobile-hidden"
            />
            <div>
              <SvgIcon
                src="/img/new-icons/rpe-custom-ico.svg"
                className="metrics-icon only-show-mobile"
              />
              <h3>{intensityMeasure} Custom Tables</h3>
              <ToggleLinksInput
                name="display"
                options={[
                  { text: "% 1RM", value: "repMax" },
                  { text: "Kg", value: "metric" },
                  { text: "Lbs", value: "imperial" },
                ]}
                className={"only-show-mobile"}
              />
              <p className="p4 mobile-hidden">
                Track your {intensityMeasure}s while you workout, then watch
                your Personal {intensityMeasure} Table update automatically to
                reflect your current levels of strength. Use the tables to know
                what intensity you should use for a particular rep range and{" "}
                {intensityMeasure}. Filter based on squat, bench, and deadlift.
              </p>
            </div>
            <p className="p4 only-show-mobile">
              Track your {intensityMeasure}s while you workout, then watch your
              Personal {intensityMeasure} Table update automatically to reflect
              your current levels of strength. Use the tables to know what
              intensity you should use for a particular rep range and{" "}
              {intensityMeasure}. Filter based on squat, bench, and deadlift.
            </p>
          </hgroup>
          <hgroup className="metrics-dates">
            <form id="metrics-form" action="/actions/refresh">
              <div role="calendar-date-selectors">
                <DateInput
                  name="start"
                  placeholder="Start Date"
                  value={query.start}
                  max={query.end}
                  onChange={submitForm}
                />
                <span>to</span>
                <DateInput
                  name="end"
                  placeholder="End Date"
                  value={query.end}
                  min={query.start}
                  onChange={submitForm}
                  alignRight={true}
                />
              </div>
              <div>
                <select
                  name="formula"
                  className="select-input"
                  onChange={submitForm}
                  defaultValue={query.formula}
                >
                  <option value="custom">
                    Your Personal {intensityMeasure} Table
                  </option>
                  <option value="standard">Industry Standard</option>
                </select>
              </div>
            </form>
          </hgroup>

          <hgroup className="metrics-data-options">
            <ToggleLinksInput
              name="display"
              options={[
                { text: "% 1RM", value: "repMax" },
                { text: "Kg", value: "metric" },
                { text: "Lbs", value: "imperial" },
              ]}
              className={"mobile-hidden"}
            />

            <ToggleLinksInput
              className="tabs"
              name="type"
              options={[
                { text: "Squat", value: "squat" },
                { text: "Bench", value: "bench" },
                { text: "Deadlift", value: "deadlift" },
              ]}
            />
          </hgroup>
        </header>

        <div className="legend">
          <div className="bar series max">Max</div>
          <div className="bar series heavy">Heavy</div>
          <div className="bar series medium">Medium</div>
          <div className="bar series light">Light</div>
          <div className="bar series deload">Deload</div>
        </div>
        <section role="table-wrapper" className="rpe-table">
          <table role="report-table">
            <thead>
              <tr>
                <th>{intensityMeasure}</th>
                <th>1 Rep</th>
                <th>2</th>
                <th>3</th>
                <th>4</th>
                <th>5</th>
                <th>6</th>
                <th>7</th>
                <th>8</th>
                <th>9</th>
                <th>10</th>
                <th>11</th>
                <th>12</th>
              </tr>
            </thead>
            <tbody>
              {_.map(RPE_RANGE, (rpe) => {
                if (!activeTable[rpe]) return;
                return (
                  <tr key={rpe}>
                    <td className="bold">{convertToRiR(rpe, !isRiR)}</td>
                    {_.map(REP_RANGE, (rep) => {
                      const result = _.get(activeTable, [rpe, rep]) || {};
                      const value = formatIntensity(
                        result.intensity,
                        result.repMax
                      );
                      return (
                        <td key={rep} className={result.zone}>
                          {result.date ? (
                            <a
                              href={
                                result.date &&
                                `/dashboard/calendar/?view=day&date=${moment
                                  .utc(result.date)
                                  .format(DATE_DAY_FORMAT)}`
                              }
                            >
                              {value}
                            </a>
                          ) : (
                            value
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      </article>
    </main>
  );

  function formatIntensity(intensity, repMax) {
    if (!intensity || intensity <= 0) return "-";
    intensity = Math.min(intensity, 100);
    if (query.display === "repMax") return `${intensity}%`;
    const defaultRepMax = repMax || activeRepMax;
    return locals.withWeight(
      locals.fromMetric((intensity * defaultRepMax) / 100, query.display),
      query.display
    );
  }
}

/**
 * Estimates RPE table based one three strategies.
 */
function calculateEstimates(table) {
  let found = 0;

  // Blow up using up / right and down / left strategory.
  found += blowOut(table);

  // Estimate values by the average for the row.
  let foundInRow = (found += estimateRow(table));
  // If we find anything in row estimates then we blowout again.
  if (foundInRow) found += blowOut(table);

  // Estimate values by the average for the column.
  found += estimateColumn(table);

  // If we found any points this run then we try again.
  if (found) return calculateEstimates(table);

  // Otherwise we can't calculate anymore.
  return table;
}

/**
 * Calculates rpe estimates by blowing out the table.
 * Shifts existing data points diagonally (up and right, down and left).
 */
function blowOut(table) {
  let found = 0;
  _.forOwn(table, (reps, rpe) => {
    _.forOwn(reps, (data, rep) => {
      if (!data || !data.intensity) return;

      let posX = null;
      let posY = null;

      // up and right
      posX = rep;
      posY = rpe;
      while (
        ++posX < MAX_CALC_REP &&
        ++posY < MAX_CALC_RPE &&
        !_.get(table, [posY, posX])
      ) {
        _.set(table, [posY, posX], {
          intensity: data.intensity,
          zone: fromIntensity(data.intensity, posX),
        });
        found++;
      }

      // down and left
      posX = rep;
      posY = rpe;
      while (
        --posX > MIN_CALC_REP &&
        --posY > MIN_CALC_RPE &&
        !_.get(table, [posY, posX])
      ) {
        _.set(table, [posY, posX], {
          intensity: data.intensity,
          zone: fromIntensity(data.intensity, posX),
        });
        found++;
      }
    });
  });

  return found;
}

/**
 * Estimates data to right of a row (don't guess to the left).
 */
function estimateRow(table) {
  let found = 0;
  _.forOwn(table, (reps, rpe) => {
    rpe = Number(rpe);
    let previousIntensity = null;
    let previousRep = null;
    let activeDelta = null;
    let estimateReps = [];
    _.forEach(CALC_REP_RANGE, (rep, i) => {
      const data = _.get(reps, rep);

      if (data) {
        if (previousIntensity) {
          activeDelta =
            Math.abs(previousIntensity - data.intensity) /
            Math.abs(previousRep - rep);
        }

        previousRep = rep;
        previousIntensity = data.intensity;
      } else if (previousIntensity) {
        estimateReps.push(rep);
      }

      if (activeDelta && (data || i === CALC_REP_RANGE.length - 1)) {
        _.forEach(estimateReps, (rep) => {
          found++;
          const intensity = _.round(
            previousIntensity - (rep - previousRep) * activeDelta,
            1
          );
          _.set(table, [rpe, rep], {
            zone: fromIntensity(intensity),
            intensity,
          });
        });

        estimateReps = [];
      }
    });
  });

  return found;
}

/**
 * Estimates data down in a column (don't guess to the above).
 */
function estimateColumn(table) {
  let found = 0;
  _.forEach(CALC_REP_RANGE, (rep) => {
    let previousIntensity = null;
    let previousRpe = null;
    let activeDelta = null;
    let estimateRpes = [];

    _.forEach(RPE_RANGE, (rpe, i) => {
      const data = _.get(table, [rpe, rep]);

      if (data) {
        if (previousIntensity) {
          activeDelta =
            Math.abs(previousIntensity - data.intensity) /
            Math.abs(previousRpe - rpe);
        }

        previousRpe = rpe;
        previousIntensity = data.intensity;
      } else if (previousIntensity) {
        estimateRpes.push(rpe);
      }

      if (activeDelta && (data || i === RPE_RANGE.length - 1)) {
        _.forEach(estimateRpes, (rpe) => {
          found++;
          const intensity = _.round(
            previousIntensity - (previousRpe - rpe) * activeDelta,
            1
          );
          _.set(table, [rpe, rep], {
            zone: fromIntensity(intensity),
            intensity,
          });
        });

        estimateRpes = [];
      }
    });
  });

  return found;
}

function submitForm() {
  submit("/actions/refresh", { body: document.getElementById("metrics-form") });
}

const RpeTable = (app) => {
  app.get(
    "/rpe-table",
    set("heading", "RPE Custom Tables"),
    async ({ req, res, load, locals, api, fail }) => {
      const { query } = req;
      _.defaults(query, {
        type: "squat",
        formula: "standard",
        display: "repMax",
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await load("exercisesRPETable", {
        start: moment
          .utc(query.start, DATE_PICKER_FORMAT)
          .startOf("day")
          .toDate(),
        end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
      });

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  type: "rpe-table",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick(["exercisesRPETable"])
                      .assign({
                        start: query.start,
                        end: query.end,
                        formula: query.formula,
                        repMax: _.get(
                          locals.activeUser,
                          `repMaximums.${query.type}`,
                          0
                        ),
                      })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);

          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }
      const { activeUser: user } = locals;
      if (user.subscription || user.coach) {
        await load("feBranding", {});
      }
      locals.modal = {
        share: <ShareModal />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default RpeTable;
